import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';

const RateLimitingSettings = ({ settings, onSettingsChange }) => {
    const [formData, setFormData] = useState(settings);
    const [isSaving, setIsSaving] = useState(false);
    const [isToggling, setIsToggling] = useState(false);

    // Update form data when settings prop changes
    useEffect(() => {
        setFormData(settings);
    }, [settings]);

    const handleInputChange = async (key, value) => {
        const newFormData = {
            ...formData,
            [key]: value
        };
        setFormData(newFormData);
        
        // Auto-save when rate limiting toggle is changed
        if (key === 'rate_limiting_enabled') {
            setIsToggling(true);
            console.log('Auto-saving rate limiting toggle change:', newFormData);
            try {
                await onSettingsChange(newFormData);
                console.log('Rate limiting toggle saved successfully');
            } catch (error) {
                console.error('Failed to save rate limiting toggle:', error);
                // Revert the change on error
                setFormData(formData);
            } finally {
                setIsToggling(false);
            }
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            console.log('Saving rate limiting settings:', formData);
            await onSettingsChange(formData);
            console.log('Rate limiting settings saved successfully');
        } catch (error) {
            console.error('Failed to save rate limiting settings:', error);
            // Error is handled in parent
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="flex flex-col gap-3 rounded">
            <div className="flex flex-row items-center">
                <h4 className="font-semibold text-font-primary flex items-center gap-1 text-base">
                    <Icon icon="mdi:cog" className="text-xl text-primary" />
                    Rate Limiting Settings
                </h4>
            </div>
            <hr className='border-border' />
            <div className="flex flex-col gap-4">
                {/* Enable Rate Limiting Toggle */}
                <div className="card-container flex flex-row items-center justify-between">
                    <div className="flex flex-row items-center gap-2">
                        <Icon 
                            icon="mdi:speedometer" 
                            className="text-2xl text-primary" 
                        />
                        <div className="flex flex-col items-start gap-1">
                            <span className="font-semibold">Enable Rate Limiting</span>
                            <p className="text-sm text-font-secondary">Control API request frequency</p>
                        </div>
                    </div>
                    <label className={`relative inline-flex items-center ${isToggling ? 'cursor-wait' : 'cursor-pointer'}`}>
                        <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={formData.rate_limiting_enabled || false}
                            disabled={isToggling}
                            onChange={(e) => {
                                console.log('Rate limiting toggle changed to:', e.target.checked);
                                handleInputChange('rate_limiting_enabled', e.target.checked);
                            }}
                        />
                        <div className={`w-11 h-6 bg-gray-400 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-md peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-md after:h-5 after:w-5 after:transition-all peer-checked:bg-primary ${isToggling ? 'opacity-50' : ''}`}>
                            {isToggling && (
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <Icon icon="eos-icons:loading" className="animate-spin text-white text-xs" />
                                </div>
                            )}
                        </div>
                    </label>
                </div>

                {/* Requests per Minute */}
                <div className="card-container flex flex-col gap-2">
                    <label className="text-sm font-semibold text-font-primary">
                        Requests per Minute
                    </label>
                    <div className="flex items-center gap-2">
                        <input
                            type="number"
                            min="1"
                            max="10000"
                            value={formData.requests_per_minute || 100}
                            onChange={(e) => handleInputChange('requests_per_minute', parseInt(e.target.value) || 100)}
                            className="flex-1 px-3 py-2 bg-background input-textbox rounded text-sm border border-border focus:border-primary focus:outline-none"
                        />
                        <span className="text-sm text-font-secondary">req/min</span>
                    </div>
                    <p className="text-sm text-font-secondary">
                        Maximum requests allowed per minute per IP
                    </p>
                </div>

                {/* Burst Limit */}
                <div className="card-container flex flex-col gap-2">
                    <label className="text-sm font-semibold text-font-primary">
                        Burst Limit
                    </label>
                    <div className="flex items-center gap-2">
                        <input
                            type="number"
                            min="1"
                            max="1000"
                            value={formData.burst_limit || 200}
                            onChange={(e) => handleInputChange('burst_limit', parseInt(e.target.value) || 200)}
                            className="flex-1 px-3 py-2 bg-background input-textbox rounded text-sm border border-border focus:border-primary focus:outline-none"
                        />
                        <span className="text-sm text-font-secondary">requests</span>
                    </div>
                    <p className="text-sm text-font-secondary">
                        Maximum burst requests allowed before rate limiting kicks in
                    </p>
                </div>

                {/* Selective Rate Limiting Info */}
                <div className="p-3 bg-blue-400 bg-opacity-20 border border-blue-500 rounded-lg">
                    <div className="flex items-center gap-1 mb-2 text-font-primary">
                        <Icon icon="mdi:information" className="text-blue-500 text-lg" />
                        <span className="text-sm font-bold">Selective Rate Limiting</span>
                    </div>
                    <p className="text-sm">
                        Only high and medium priority routes are rate limited. Read-only and low-risk routes remain unlimited for optimal performance.
                    </p>
                </div>

                {/* Admin Bypass Info */}
                <div className="p-3 bg-yellow-400 bg-opacity-20 border border-yellow-500 rounded-lg">
                    <div className="flex items-center gap-1 mb-1 text-font-primary">
                        <Icon icon="mdi:shield-crown" className="text-yellow-500 text-lg" />
                        <span className="text-sm font-bold">Admin Bypass</span>
                    </div>
                    <p className="text-sm">
                        Helios admins bypass all rate limits for administrative operations.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default RateLimitingSettings;
